const SAFE_GENERATION_PATTERN = /^v1:[A-Za-z0-9:._-]{1,180}$/;
const SESSION_GENERATION_PREFIX = 'keilog-sso.session-generation:';
const PASSIVE_MARKER_PREFIX = 'keilog-sso.passive:';
const SSO_LOGIN_MARKERS = new Set([
	'sso_active_complete',
	'sso_complete',
	'sso_denied',
	'sso_error',
	'sso_passive',
]);

function siteKey(context) {
	if (!context || !context.enabled || !context.site) {
		throw new Error('SSO site context is unavailable.');
	}
	return `${context.site.tenant_code}:${context.site.service_code}`;
}

function storageKey(prefix, context) {
	return `${prefix}${siteKey(context)}`;
}

function safeStorageRead(storage, key) {
	try {
		return storage.getItem(key);
	} catch (error) {
		return null;
	}
}

function markPassiveCheck(context, storage, now = Date.now()) {
	storage.setItem(storageKey(PASSIVE_MARKER_PREFIX, context), String(now));
}

function clearPassiveCheck(context, storage) {
	try {
		storage.removeItem(storageKey(PASSIVE_MARKER_PREFIX, context));
	} catch (error) {
		// A blocked storage area is already handled by the login flow's safe local fallback.
	}
}

function shouldStartPassiveCheck({ context, hasLocalSession, search, storage, now = Date.now() }) {
	if (!context.enabled || !context.passive_login_enabled || hasLocalSession) {
		return false;
	}
	if (new URLSearchParams(search).get('sso_passive') === 'checked') {
		return false;
	}

	const previousValue = safeStorageRead(storage, storageKey(PASSIVE_MARKER_PREFIX, context));
	const previous = previousValue === null ? Number.NaN : Number(previousValue);
	return !Number.isFinite(previous) || now - previous >= context.passive_retry_seconds * 1000;
}

function isPassiveSsoCheckedReturn(search) {
	return new URLSearchParams(search).get('sso_passive') === 'checked';
}

function shouldStartPassiveCheckOnMount({ passiveWasChecked, ...options }) {
	return !passiveWasChecked && shouldStartPassiveCheck(options);
}

function shouldResumeLocalSessionOnLogin({ ssoEnabled, hasLocalSession, search }) {
	if (!ssoEnabled || !hasLocalSession) return false;
	const params = new URLSearchParams(search);
	return !Array.from(params.keys()).some(name => SSO_LOGIN_MARKERS.has(name));
}

async function startPassiveSsoCheck({
	context,
	storage,
	mutationCoordinator,
	navigate,
	now = Date.now,
}) {
	if (!mutationCoordinator || typeof mutationCoordinator.run !== 'function' || typeof navigate !== 'function') {
		throw new Error('SSO passive login coordination is unavailable.');
	}

	const result = await mutationCoordinator.run(async () => {
		// The marker belongs to an acquired mutation turn. Writing it before Web Locks accepted the
		// operation poisoned a later tab even when this turn was stale or unavailable.
		markPassiveCheck(context, storage, now());
		try {
			navigate(context.endpoints.passive_start);
		} catch (error) {
			clearPassiveCheck(context, storage);
			throw error;
		}
	});

	return result.outcome === 'applied';
}

function stripOneShotQueryMarkers(urlValue, markerNames, historyObject = window.history) {
	const url = new URL(urlValue);
	let changed = false;
	markerNames.forEach((name) => {
		if (url.searchParams.has(name)) {
			url.searchParams.delete(name);
			changed = true;
		}
	});
	if (changed) {
		historyObject.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
	}
}

function safeReturnPath(value, origin = window.location.origin) {
	if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || value.length > 2048 || value.includes('\\')) {
		return null;
	}

	try {
		const parsed = new URL(value, origin);
		if (parsed.origin !== origin || parsed.username !== '' || parsed.password !== '') {
			return null;
		}
		return `${parsed.pathname}${parsed.search}${parsed.hash}`;
	} catch (error) {
		return null;
	}
}

function safeNavigationUrl(value, { origin = window.location.origin, allowExternalHttps = false } = {}) {
	if (typeof value !== 'string' || value.trim() === '') {
		return null;
	}

	try {
		const parsed = new URL(value, origin);
		if (parsed.username !== '' || parsed.password !== '' || parsed.hash !== '') {
			return null;
		}
		if (parsed.origin === origin) {
			return parsed.toString();
		}
		return allowExternalHttps && parsed.protocol === 'https:' ? parsed.toString() : null;
	} catch (error) {
		return null;
	}
}

function safeLogoutUrl(value, loginPath, origin = window.location.origin) {
	const destination = safeNavigationUrl(value, { origin, allowExternalHttps: true });
	if (!destination) return null;

	const parsed = new URL(destination);
	if (parsed.origin !== origin) return destination;
	return parsed.pathname === loginPath && parsed.search === '' ? destination : null;
}

function pendingSsoEstablishUrl(envelope, origin = window.location.origin, now = Date.now()) {
	if (!envelope || envelope.status !== 'pending') return null;
	const sso = envelope.data && envelope.data.sso;
	const expiresAt = sso && sso.session_expires_at;
	const nowSeconds = Math.floor(now / 1000);
	if (
		envelope.code !== 202
		|| Object.prototype.hasOwnProperty.call(envelope.data || {}, 'access_token')
		|| !sso
		|| sso.auth_kind !== 'sso_pending'
		|| !Number.isInteger(expiresAt)
		|| expiresAt <= nowSeconds
	) {
		throw new Error('Invalid pending SSO login response.');
	}

	const destination = safeNavigationUrl(sso.establish_url, {
		origin,
		allowExternalHttps: true,
	});
	if (!destination) throw new Error('Invalid SSO establish URL.');
	return destination;
}

function randomGeneration() {
	if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
		return `v1:${globalThis.crypto.randomUUID()}`;
	}
	return `v1:${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

function readAuthGeneration(context, storage) {
	const value = safeStorageRead(storage, storageKey(SESSION_GENERATION_PREFIX, context));
	return typeof value === 'string' && SAFE_GENERATION_PATTERN.test(value) ? value : 'v1:initial';
}

function advanceAuthGeneration(context, storage) {
	const generation = randomGeneration();
	storage.setItem(storageKey(SESSION_GENERATION_PREFIX, context), generation);
	return generation;
}

function captureAuthGeneration(context, storage) {
	return {
		key: siteKey(context),
		generation: readAuthGeneration(context, storage),
	};
}

function isAuthGenerationCurrent(snapshot, context, storage) {
	return snapshot && snapshot.key === siteKey(context) && snapshot.generation === readAuthGeneration(context, storage);
}

function isUserReload(performanceObject = window.performance) {
	try {
		const entries = performanceObject.getEntriesByType('navigation');
		return entries.length > 0 && entries[0].type === 'reload';
	} catch (error) {
		return false;
	}
}

module.exports = {
	advanceAuthGeneration,
	captureAuthGeneration,
	clearPassiveCheck,
	isAuthGenerationCurrent,
	isUserReload,
	isPassiveSsoCheckedReturn,
	markPassiveCheck,
	pendingSsoEstablishUrl,
	readAuthGeneration,
	safeNavigationUrl,
	safeLogoutUrl,
	safeReturnPath,
	shouldResumeLocalSessionOnLogin,
	shouldStartPassiveCheck,
	shouldStartPassiveCheckOnMount,
	startPassiveSsoCheck,
	siteKey,
	stripOneShotQueryMarkers,
};
